import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, HelpCircle, Loader2, CreditCard, Smartphone, Building2, Wallet } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { useToast } from '../hooks/use-toast';

const PAYMENT_METHODS = [
  { id: 'bank', label: 'Bank Transfer', icon: <Building2 className="w-5 h-5" />, color: 'blue' },
];

const AddAccountPage = () => {
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [formData, setFormData] = useState({
    bankName: '', accountHolderName: '', accountNumber: '', ifscCode: '',
    upiId: '', phoneNumber: '', withdrawPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [ifscLoading, setIfscLoading] = useState(false);
  const [branchInfo, setBranchInfo] = useState(null);
  const { toast } = useToast();

  const validateForm = () => {
    const e = {};
    if (paymentMethod === 'bank') {
      if (!formData.bankName || formData.bankName === 'Select Bank Name') e.bankName = 'Please enter bank name';
      if (!formData.accountHolderName.trim()) e.accountHolderName = 'Please enter account holder name';
      if (!formData.accountNumber.trim()) e.accountNumber = 'Please enter account number';
      else if (formData.accountNumber.length < 9 || formData.accountNumber.length > 18) e.accountNumber = 'Account number must be 9-18 digits';
      if (!formData.ifscCode.trim()) e.ifscCode = 'Please enter IFSC code';
      else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) e.ifscCode = 'Invalid IFSC code format';
    } else if (paymentMethod === 'upi') {
      if (!formData.upiId.trim()) e.upiId = 'Please enter UPI ID';
      else if (!formData.upiId.includes('@')) e.upiId = 'Invalid UPI ID (e.g. name@upi)';
      if (!formData.accountHolderName.trim()) e.accountHolderName = 'Please enter account holder name';
    } else {
      // PhonePe, Google Pay, Paytm
      if (!formData.phoneNumber.trim()) e.phoneNumber = 'Please enter phone number';
      else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/\D/g, ''))) e.phoneNumber = 'Enter valid 10-digit number';
      if (!formData.accountHolderName.trim()) e.accountHolderName = 'Please enter name';
    }
    if (!formData.withdrawPassword.trim()) e.withdrawPassword = 'Please enter password';
    if (!agreeTerms) e.terms = 'Please agree to the terms';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setErrors({});
    try {
      const token = localStorage.getItem('reddyannabook_token');
      if (!token) { navigate('/login'); return; }

      const methodLabel = PAYMENT_METHODS.find(m => m.id === paymentMethod)?.label || paymentMethod;
      const requestData = {
        payment_method: paymentMethod,
        account_holder_name: formData.accountHolderName,
        withdraw_password: formData.withdrawPassword,
      };

      if (paymentMethod === 'bank') {
        requestData.bank_name = formData.bankName;
        requestData.account_number = formData.accountNumber;
        requestData.ifsc_code = formData.ifscCode;
      } else if (paymentMethod === 'upi') {
        requestData.bank_name = 'UPI';
        requestData.account_number = formData.upiId;
        requestData.ifsc_code = 'UPI';
      } else {
        requestData.bank_name = methodLabel;
        requestData.account_number = formData.phoneNumber;
        requestData.ifsc_code = paymentMethod.toUpperCase();
      }

      const response = await fetch(API_ENDPOINTS.WALLET_ADD_ACCOUNT, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        toast({ title: "Success", description: `${methodLabel} account added successfully!` });
        navigate('/withdraw');
      } else {
        throw new Error(data.message || 'Failed to add account');
      }
    } catch (err) {
      setErrors({ general: err.message });
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const fetchIFSCDetails = async (ifscCode) => {
    if (!ifscCode || ifscCode.length !== 11) { setBranchInfo(null); return; }
    setIfscLoading(true);
    try {
      const res = await fetch(`https://ifsc.razorpay.com/${ifscCode}`);
      const data = await res.json();
      if (data.BANK && data.BRANCH) {
        setBranchInfo({ bank: data.BANK, branch: data.BRANCH, city: data.CITY, state: data.STATE });
        setFormData(prev => ({ ...prev, bankName: data.BANK }));
      } else {
        setBranchInfo(null);
      }
    } catch { setBranchInfo(null); }
    finally { setIfscLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-6 pb-32">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-900">Add Withdrawal Account</h1>
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/withdraw')} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2">
              Use Previous Accounts
            </Button>
            <div className="flex items-center gap-1 text-gray-700">
              <span>Help</span><HelpCircle className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Payment Method Selector */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <h2 className="text-sm font-bold text-gray-700 mb-3">Select Payment Method</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PAYMENT_METHODS.map(method => (
              <button
                key={method.id}
                type="button"
                onClick={() => { setPaymentMethod(method.id); setErrors({}); setBranchInfo(null); }}
                className={`flex items-center gap-2 px-3 py-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                  paymentMethod === method.id
                    ? 'border-green-500 bg-green-50 text-green-700 shadow-sm'
                    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {method.icon}
                {method.label}
              </button>
            ))}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-4 space-y-4">

          {/* ─── Bank Transfer Fields ─── */}
          {paymentMethod === 'bank' && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Bank Name</label>
                <Input type="text" value={formData.bankName} onChange={(e) => handleInputChange('bankName', e.target.value)}
                  placeholder="Will be auto-filled from IFSC code" className="h-12" readOnly={branchInfo !== null} />
                {branchInfo && <p className="text-xs text-green-600 mt-1">✓ Auto-filled from IFSC</p>}
                {errors.bankName && <p className="text-red-600 text-sm mt-1">{errors.bankName}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Account Holder Name</label>
                <Input type="text" placeholder="Eg. John Doe" value={formData.accountHolderName}
                  onChange={(e) => handleInputChange('accountHolderName', e.target.value)} className="h-12" />
                {errors.accountHolderName && <p className="text-red-600 text-sm mt-1">{errors.accountHolderName}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Account Number</label>
                <Input type="text" placeholder="Enter Account Number" value={formData.accountNumber}
                  onChange={(e) => handleInputChange('accountNumber', e.target.value.replace(/\D/g, ''))} className="h-12" maxLength="18" />
                {errors.accountNumber && <p className="text-red-600 text-sm mt-1">{errors.accountNumber}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">IFSC Code</label>
                <div className="relative">
                  <Input type="text" placeholder="Eg. SBIN0001234" value={formData.ifscCode}
                    onChange={(e) => { const v = e.target.value.toUpperCase(); handleInputChange('ifscCode', v); if (v.length === 11) fetchIFSCDetails(v); else setBranchInfo(null); }}
                    className="h-12 pr-10" maxLength="11" />
                  {ifscLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-gray-400" />}
                </div>
                {branchInfo && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                    <span className="font-semibold">{branchInfo.branch}</span> — {branchInfo.city}, {branchInfo.state}
                  </div>
                )}
                {errors.ifscCode && <p className="text-red-600 text-sm mt-1">{errors.ifscCode}</p>}
              </div>
            </>
          )}

          {/* ─── UPI Fields ─── */}
          {paymentMethod === 'upi' && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Account Holder Name</label>
                <Input type="text" placeholder="Eg. John Doe" value={formData.accountHolderName}
                  onChange={(e) => handleInputChange('accountHolderName', e.target.value)} className="h-12" />
                {errors.accountHolderName && <p className="text-red-600 text-sm mt-1">{errors.accountHolderName}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">UPI ID</label>
                <Input type="text" placeholder="Eg. yourname@upi or 9876543210@paytm" value={formData.upiId}
                  onChange={(e) => handleInputChange('upiId', e.target.value)} className="h-12" />
                <p className="text-xs text-gray-500 mt-1">Enter your UPI ID linked to your bank account</p>
                {errors.upiId && <p className="text-red-600 text-sm mt-1">{errors.upiId}</p>}
              </div>
            </>
          )}

          {/* ─── PhonePe / Google Pay / Paytm Fields ─── */}
          {['phonepe', 'googlepay', 'paytm'].includes(paymentMethod) && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                <Input type="text" placeholder="Eg. John Doe" value={formData.accountHolderName}
                  onChange={(e) => handleInputChange('accountHolderName', e.target.value)} className="h-12" />
                {errors.accountHolderName && <p className="text-red-600 text-sm mt-1">{errors.accountHolderName}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {paymentMethod === 'phonepe' ? 'PhonePe' : paymentMethod === 'googlepay' ? 'Google Pay' : 'Paytm'} Registered Mobile Number
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-600 bg-gray-100 px-3 py-3 rounded-l-md border border-r-0 border-gray-300">+91</span>
                  <Input type="tel" placeholder="9876543210" value={formData.phoneNumber}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value.replace(/\D/g, '').slice(0, 10))} className="h-12 rounded-l-none" maxLength="10" />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter the mobile number registered with {paymentMethod === 'phonepe' ? 'PhonePe' : paymentMethod === 'googlepay' ? 'Google Pay' : 'Paytm'}
                </p>
                {errors.phoneNumber && <p className="text-red-600 text-sm mt-1">{errors.phoneNumber}</p>}
              </div>
            </>
          )}

          {/* ─── Common Fields ─── */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Password Verification</label>
            <div className="relative">
              <Input type={showPassword ? 'text' : 'password'} placeholder="Enter your login password"
                value={formData.withdrawPassword} onChange={(e) => handleInputChange('withdrawPassword', e.target.value)} className="h-12 pr-10" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">Enter your account login password to verify this action</p>
            {errors.withdrawPassword && <p className="text-red-600 text-sm mt-1">{errors.withdrawPassword}</p>}
          </div>

          <label className="flex items-start gap-3">
            <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} className="mt-1" />
            <span className="text-sm text-gray-700">
              I have read and agree with <span className="underline text-blue-600">the terms of payment</span> and <span className="underline text-blue-600">withdrawal policy</span>
            </span>
          </label>
          {errors.terms && <p className="text-red-600 text-sm">{errors.terms}</p>}

          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-700 text-sm">{errors.general}</p>
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full bg-green-600 hover:bg-green-700 text-white h-12">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding Account...</> : 'Proceed'}
          </Button>
        </form>

        {/* Rules */}
        <div className="bg-white border-2 border-gray-900 rounded-lg p-4">
          <ol className="space-y-2 text-sm text-gray-900">
            <li><strong>1.</strong> This form is for withdrawing the amount from the main wallet only.</li>
            <li><strong>2.</strong> The bonus wallet amount cannot be withdrawn by this form.</li>
            <li><strong>3.</strong> Do not put Withdraw request without betting with deposit amount. Such activity may be identified as Suspicious</li>
            <li><strong>4.</strong> If multiple users are using same withdraw account then all the linked users will be blocked.</li>
            <li><strong>5.</strong> Maximum Withdraw time is 45 minutes then only complain on WhatsApp number.</li>
            <li><strong>6.</strong> Withdrawal account should be same to deposit account.</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default AddAccountPage;
